import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Globe, 
  Lightbulb,
  Users, 
  Award,
  Recycle,
  TrendingUp,
  Truck,
  Cpu,
  Mountain,
  MessageCircle,
  Scale,
  GraduationCap
} from 'lucide-react';

const WhatIPMIConnects = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const industryTypes = [
    { 
      icon: Recycle, 
      title: "Recycling & Refining", 
      description: "From scrap and concentrates to high-purity metal" 
    },
    { 
      icon: TrendingUp, 
      title: "Trading & Finance", 
      description: "Pricing, liquidity, and risk across precious metals markets" 
    },
    { 
      icon: Truck, 
      title: "Logistics & Storage", 
      description: "Secure movement, custody, and vaulting solutions" 
    },
    { 
      icon: Cpu, 
      title: "Technology & Manufacturing", 
      description: "Innovation that improves processes and performance" 
    },
    { 
      icon: Mountain, 
      title: "Mining & Supply", 
      description: "Producers and upstream specialists shaping supply" 
    },
    { 
      icon: MessageCircle, 
      title: "Consulting & Advisory", 
      description: "Independent expertise that helps organisations decide" 
    },
    { 
      icon: Scale, 
      title: "Legal & Compliance", 
      description: "Guidance that supports trust and stability" 
    },
    { 
      icon: GraduationCap, 
      title: "Research & Education", 
      description: "Science, learning, and the next generation of talent" 
    },
  ];

  const reasons = [
    { 
      icon: Globe, 
      title: "Global Community", 
      description: "Meet professionals across the full precious metals value chain" 
    },
    { 
      icon: Lightbulb, 
      title: "Knowledge & Insights", 
      description: "Stay current with research, technical programming, and industry events" 
    },
    { 
      icon: Users, 
      title: "Meaningful Connections", 
      description: "Build relationships that strengthen your career and your business" 
    },
    { 
      icon: Award, 
      title: "Recognised Excellence", 
      description: "Awards and recognition that celebrate achievement and support future talent" 
    },
  ];

  return (
    <section className="what-ipmi-connects-section" ref={sectionRef}>
      {/* Animated connection lines background */}
      <div className="connections-background">
        <svg className="connections-svg" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
          {/* Horizontal connection lines */}
          <motion.path
            d="M100,150 Q300,120 500,150 T900,150"
            className="connection-line"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
          />
          <motion.path
            d="M100,300 Q400,330 500,300 T900,300"
            className="connection-line"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2, delay: 0.7, ease: "easeInOut" }}
          />
          <motion.path
            d="M100,450 Q250,420 500,450 T900,450"
            className="connection-line"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2, delay: 0.9, ease: "easeInOut" }}
          />
          {/* Diagonal connections */}
          <motion.path
            d="M150,100 Q500,300 850,500"
            className="connection-line diagonal"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2.5, delay: 1.1, ease: "easeInOut" }}
          />
          <motion.path
            d="M850,100 Q500,300 150,500"
            className="connection-line diagonal"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ duration: 2.5, delay: 1.3, ease: "easeInOut" }}
          />
          
          {/* Animated glowing dots traveling along paths */}
          <motion.circle
            r="4"
            className="traveling-dot"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: [0, 1, 1, 0] } : {}}
            transition={{ duration: 3, delay: 2, repeat: Infinity, repeatDelay: 1 }}
          >
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              path="M100,150 Q300,120 500,150 T900,150"
              begin="2s"
            />
          </motion.circle>
          <motion.circle
            r="4"
            className="traveling-dot"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: [0, 1, 1, 0] } : {}}
            transition={{ duration: 4, delay: 2.5, repeat: Infinity, repeatDelay: 1.5 }}
          >
            <animateMotion
              dur="4s"
              repeatCount="indefinite"
              path="M150,100 Q500,300 850,500"
              begin="2.5s"
            />
          </motion.circle>
        </svg>
      </div>

      <div className="section-container">
        {/* Industry Types Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="section-header centered"
        >
          <h2 className="section-title-large">
            Who We Connect
          </h2>
          <p className="section-subtitle">
            Bringing together professionals across the precious metals value chain for <strong className="highlight-gold">50 years</strong>
          </p>
        </motion.div>

        {/* Industry Cards Grid */}
        <motion.div
          className="industry-cards-grid"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {industryTypes.map((industry, index) => {
            const IconComponent = industry.icon;
            return (
              <motion.div
                key={industry.title}
                className="industry-card"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.3 + (index * 0.1),
                  ease: "easeOut"
                }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="industry-card-icon">
                  <IconComponent size={28} />
                </div>
                <h3 className="industry-card-title">{industry.title}</h3>
                <p className="industry-card-desc">{industry.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Divider */}
        <motion.div 
          className="section-divider-line"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
        />

        {/* Why Connect Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="section-header centered"
        >
          <h2 className="section-title-large">
            Why Connect With Us?
          </h2>
        </motion.div>

        {/* Reasons Grid */}
        <motion.div
          className="why-connect-grid"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            return (
              <motion.div
                key={reason.title}
                className="why-connect-card"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.5, 
                  delay: 1.1 + (index * 0.15),
                  ease: "easeOut"
                }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="why-connect-icon">
                  <IconComponent size={32} />
                </div>
                <h3 className="why-connect-title">{reason.title}</h3>
                <p className="why-connect-desc">{reason.description}</p>
                <div className="card-glow"></div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="connection-emphasis"
        >
          <p className="emphasis-tagline">
            Where the precious metals world comes together
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatIPMIConnects;
